import { useMemo, useState } from 'react'
import ToggleSwitch from '@components/common/ToggleSwitch'
import { fmtDayColumn, fmtDayRange, fmtTimeInZone } from './queueFormat'
import { firingsInWindow, startOfDay } from './scheduleFormat'

/**
 * The beta schedule calendar (design screen 6, bottom), scoped exactly as Jordan
 * asked: "I'll throw it at the bottom of this page and hide it under a checkbox
 * that says show schedule… I'm beta testing anyway."
 *
 * So: bottom of the page, **collapsed by default**, labelled as a beta, with a
 * feedback link — because Himaad ranked the notification pattern above this for
 * MVP ("less is more for the first run"), and an unopened panel costs nothing while
 * still letting us find out whether anyone wants it.
 *
 * ## Why a week view, and why it is honest about its window
 *
 * The point is collision-spotting: "so you can see when heavy reports stack up on
 * the same morning". A week is the smallest window in which that is visible for
 * daily and weekly schedules. Monthly schedules will often fall outside it, so the
 * panel says which week it is showing and how many schedules have no run in it,
 * rather than implying it shows everything.
 *
 * Firings are computed from `nextRunAt` plus the recurrence's own interval for
 * daily/weekly rules only. It deliberately does NOT re-implement the monthly
 * calendar maths: `recurrence.js` on the server is the single engine for that (the
 * editor's own preview is server-computed for exactly this reason), and a second
 * implementation that disagrees about "the last Friday" is the bug nobody notices
 * until month-end. Monthly schedules appear only when their single known next run
 * falls inside the week.
 *
 * ## Zones: times are the SCHEDULE's, day columns are the VIEWER's
 *
 * Each entry's time is rendered in its own schedule's zone (`fmtTimeInZone`), so a
 * Chicago 07:00 schedule reads "7:00 AM" here exactly as it does on the row above —
 * the alternative puts "8:00 AM" on the calendar directly under a row saying
 * "7:00 AM CDT", i.e. one screen contradicting itself.
 *
 * Day columns, by contrast, are the viewer's calendar days, because the question
 * this panel answers is about ABSOLUTE load ("do three heavy reports hit the
 * database at once?"), and bucketing by each schedule's own zone would scatter a
 * single real collision across two columns. In a single-firm, single-zone tenant the
 * two models coincide; across zones the time label is the authoritative one.
 */

const DAY_MS = 86_400_000

/**
 * The seven day columns, stepped by CALENDAR day rather than by 86,400,000ms.
 *
 * The flat-`DAY_MS` version was measurably wrong on a DST fall-back week (QA
 * follow-up, 2026-09-03). A fall-back day is 25 hours long, so `from + 1*DAY_MS`
 * lands at 23:00 of the SAME day: on the week of 2026-11-01 the grid rendered
 * "Sun 1" twice, gave Friday no column at all, and five of the seven columns
 * showed their runs under the wrong day. A reader who saw three heavy reports
 * stacked under a duplicated Sunday would move a job off the wrong morning —
 * which is the one thing this panel exists to help them get right.
 *
 * `setDate(+1)` is DST-correct by construction: it steps the local calendar and
 * lets the runtime resolve the offset. `startOfDay` then re-normalises, so a run
 * bucketed by the same function lands in the column that shares its local day.
 */
function dayStarts(from) {
  const out = []
  const d = new Date(from)
  for (let i = 0; i < 7; i++) {
    out.push(startOfDay(d.getTime()))
    d.setDate(d.getDate() + 1)
  }
  return out
}

const ScheduleCalendarPanel = ({ schedules = [], now: nowProp, onSendFeedback }) => {
  const [open, setOpen] = useState(false)
  // A lazy useState initializer rather than `Date.now()` in the signature: an
  // impure call during render both trips the purity lint and would silently
  // re-window the calendar on any unrelated re-render.
  const [nowAtMount] = useState(() => Date.now())
  const now = nowProp ?? nowAtMount

  const week = useMemo(() => {
    const from = startOfDay(now)
    const starts = dayStarts(from)
    // The window's exclusive end is the start of the day AFTER the last column,
    // derived from the same calendar stepping rather than `from + 7*DAY_MS` — on a
    // DST week those two differ by an hour, and `firingsInWindow` would then miss
    // or double-count a run at the edge.
    const endDate = new Date(starts[6])
    endDate.setDate(endDate.getDate() + 1)
    const to = startOfDay(endDate.getTime())
    const days = starts.map((at) => ({ at, runs: [] }))
    // Bucket by matching the run's own day-start against the column's, instead of
    // dividing an elapsed-ms difference by DAY_MS. Same reason as above: across a
    // DST boundary that division is off by an hour and rounds into the wrong column.
    const indexOfDay = new Map(starts.map((at, i) => [at, i]))
    let missing = 0
    for (const s of schedules) {
      const hits = firingsInWindow(s, from, to)
      if (s.enabled && hits.length === 0) missing += 1
      for (const at of hits) {
        const idx = indexOfDay.get(startOfDay(at))
        if (idx != null) days[idx].runs.push({ at, schedule: s })
      }
    }
    for (const d of days) d.runs.sort((a, b) => a.at - b.at)
    return { from, to, days, missing }
  }, [schedules, now])

  const busiest = Math.max(...week.days.map((d) => d.runs.length), 0)

  return (
    <div className="mt-4 rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5">
            <ToggleSwitch checked={open} onChange={() => setOpen((o) => !o)} aria-label="Show the schedule calendar" />
          </div>
          <div>
            <span className="text-[13px] font-semibold text-cb-navy">Show the schedule calendar</span>
            <span className="ml-2 rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
              Beta — tell us if this is useful
            </span>
            <p className="mt-1 text-[11.5px] text-gray-500">
              A week view of every scheduled run, so you can see when heavy reports stack up on the
              same morning.
            </p>
          </div>
        </div>
        {/* Rendered only when there is somewhere for the feedback to GO. A button
            that captures nothing is worse than no button — the whole reason this
            link exists is to make the beta answer capturable rather than
            anecdotal, and a dead one quietly guarantees the opposite. */}
        {onSendFeedback && (
          <button type="button" onClick={onSendFeedback} className="btn-cb-toolbar">Send feedback</button>
        )}
      </div>

      {open && (
        <div className="mt-3.5 border-t border-gray-100 pt-3.5">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
            {week.days.map((d) => {
              const heavy = d.runs.length > 1 && d.runs.length === busiest
              return (
                <div
                  key={d.at}
                  className={`rounded-lg border p-2 ${heavy ? 'border-amber-200 bg-amber-50' : 'border-gray-100 bg-gray-50'}`}
                >
                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">
                    {fmtDayColumn(d.at)}
                  </div>
                  {d.runs.length === 0 ? (
                    /* gray-500, not gray-400 — spec 118 §D1. This em-dash sits on the
                       tile's own #f9fafb (#fffbeb is unreachable for a DASH: that tint
                       needs runs.length > 1 and the dash needs runs.length === 0). At
                       the hexes tailwindcss 4.2.1 emits, gray-400 #99a1af is 2.490:1
                       there and gray-500 #6a7282 is 4.628:1, against SC 1.4.3's 4.5. */
                    <div className="mt-1.5 text-[11px] text-gray-500">—</div>
                  ) : (
                    <ul className="mt-1.5 space-y-1">
                      {d.runs.map((r) => (
                        <li key={`${r.schedule.id}-${r.at}`} className="text-[11px] leading-snug text-cb-navy">
                          <span className="font-medium">{fmtTimeInZone(r.at, r.schedule.timezone)}</span>{' '}
                          <span className="text-gray-600">{r.schedule.name || `#${r.schedule.reportId}`}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )
            })}
          </div>
          {/* Say what the window does NOT cover, rather than implying completeness.
              The DATES are new (QA, 2026-09-03): `week.from`/`week.to` were computed
              and thrown away, so nothing on screen anchored the seven columns to a
              month. This is the half that makes a rolling week straddling a month —
              or a year — unambiguous even at a glance, independently of the
              per-column labels. */}
          <p className="mt-3 text-[11.5px] text-gray-500">
            Showing the next 7 days ({fmtDayRange(week.from, week.to)}).
            {week.missing > 0
              ? ` ${week.missing} active schedule${week.missing === 1 ? '' : 's'} ${week.missing === 1 ? 'has' : 'have'} no run in this window — monthly schedules usually fall outside it.`
              : ''}
          </p>
        </div>
      )}
    </div>
  )
}

export default ScheduleCalendarPanel
